<template>
  <v-app id="inspire" style="background: #f8f9fb">
    <v-main class="image">
      <v-container class="fill-height">
        <v-row align="center" justify="center">
          <v-col cols="12" sm="12" md="6">
            <app-header />
            <v-card class="auth-card mx-auto" max-width="460" elevation="0">
              <div class="auth-head">
                <h2 class="auth-title">Verify it's you</h2>
                <p class="auth-sub">Enter the OTP sent to {{ maskedEmail }}</p>
              </div>

              <v-otp-input
                v-model="otp"
                length="6"
                type="number"
                class="otp-boxes"
                :disabled="loading"
                @finish="otpFinished"
              />
              <v-form @submit.prevent="confirmOTP" v-model="valid" ref="form">
                <div class="d-flex justify-end mb-4">
                  <v-btn
                    :disabled="cooldown > 0"
                    variant="text"
                    text
                    class="auth-link mt-2 px-3"
                    @click="handleResendOTP"
                  >
                    {{
                      cooldown > 0 ? `Resend OTP in ${cooldown}s` : 'Resend OTP'
                    }}
                  </v-btn>
                </div>
              </v-form>
              <v-btn
                size="large"
                large
                type="submit"
                class="auth-btn"
                :loading="loading"
                :disabled="!valid || otp.length !== 6"
                block
                @click="confirmOTP"
              >
                Confirm OTP
              </v-btn>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-main>
    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="3000"
      top
      right
    >
      {{ snackbarText }}
    </v-snackbar>
    <app-bottom />
  </v-app>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import axios from 'axios'

export default {
  components: {
    appHeader: () => import('@/components/Logo'),
    appBottom: () => import('@/components/BottomImage'),
  },
  name: 'OTPConfirmation',
  data() {
    return {
      valid: false,
      otp: '',
      cooldown: 0,
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'success',
    }
  },
  computed: {
    ...mapState({
      loading: (state) => state.loading,
      email: (state) => state.auth.userEmail,
    }),
    ...mapGetters('auth', ['hasAuthenticationStatus', 'authenticationStatus']),
    maskedEmail() {
      if (!this.email) return ''
      const [name, domain] = this.email.split('@')
      return `${name[0]}${'*'.repeat(name.length - 2)}${
        name[name.length - 1]
      }@${domain}`
    },
  },
  methods: {
    ...mapActions('auth', ['validateLoginOTP']),
    otpFinished() {
      // All 6 digits entered — mark valid and auto-submit for verification.
      this.valid = true
      this.confirmOTP()
    },
    async confirmOTP() {
      if (!this.valid || this.otp.length !== 6) return

      this.$store.commit('SET_LOADING', true)

      try {
        await this.validateLoginOTP({ email: this.email, otp: this.otp })
        if (this.hasAuthenticationStatus) {
          if (this.authenticationStatus.variant === 'error') {
            this.showSnackbar(this.authenticationStatus.message, 'error')
          } else {
            this.$router.push({ name: 'Dashboard' })
          }
        }
      } catch {
        this.showSnackbar('Error confirming OTP', 'error')
      } finally {
        this.$store.commit('SET_LOADING', false)
      }
    },
    async handleResendOTP() {
      if (this.cooldown > 0) return

      try {
        const response = await axios.post(`/otp/generate`, {
          appCode: '',
          emailAddress: this.email,
          mobileNumber: '',
          otp: '',
        })

        if (response.data.data === 'succeeded') {
          this.showSnackbar('OTP resent successfully', 'success')
          this.startCooldown()
        } else {
          throw new Error('Failed to resend OTP')
        }
      } catch {
        this.showSnackbar('Error resending OTP', 'error')
      }
    },
    startCooldown() {
      this.cooldown = 30
      const timer = setInterval(() => {
        this.cooldown--
        if (this.cooldown <= 0) {
          clearInterval(timer)
        }
      }, 1000)
    },
    showSnackbar(text, color = 'success') {
      this.snackbarText = text
      this.snackbarColor = color
      this.snackbar = true
    },
  },
  mounted() {
    if (!this.email) {
      this.$router.push({ name: 'Login' })
    }
  },
}
</script>

<style scoped>
.auth-card {
  border-radius: 18px;
  border: 1px solid #e8edf5;
  background: #ffffff;
  box-shadow: 0 18px 48px rgba(15, 42, 85, 0.12);
  padding: 28px 30px 30px;
}
.auth-head {
  text-align: center;
  margin-bottom: 18px;
}
.auth-title {
  font-size: 22px;
  font-weight: 800;
  color: #0f2a55;
  margin: 0;
}
.auth-sub {
  font-size: 13px;
  color: #6b7a90;
  margin-top: 4px;
}
.auth-link {
  color: #034ea2;
  font-weight: 600;
  font-size: 13px;
  text-decoration: none;
}
.auth-link:hover {
  text-decoration: underline;
}
.auth-btn {
  background: linear-gradient(135deg, #034ea2 0%, #0a63c4 100%) !important;
  color: #ffffff !important;
  border-radius: 10px;
  font-weight: 700;
  letter-spacing: 0.3px;
  box-shadow: 0 8px 20px rgba(3, 78, 162, 0.3);
}
.auth-btn.v-btn--disabled {
  background: #e6ebf3 !important;
  color: #4b5a70 !important;
  box-shadow: none;
}
/* Larger, clearer OTP digits */
.otp-boxes ::v-deep input {
  font-size: 26px;
  font-weight: 700;
  color: #0f2a55;
}

/* Soft rounded inputs */
.auth-card ::v-deep .v-text-field--outlined .v-input__slot {
  border-radius: 10px;
}
.auth-card ::v-deep .v-text-field--outlined fieldset {
  border-color: #d6deea;
}
.auth-card ::v-deep .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: #034ea2;
}
</style>
